import React from "react";
import { AlertTriangle, Bell, Clock, Loader2, PlayCircle, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { apiTvwKejadian, apiTvwLayar } from "@/lib/publicApi";
import { antre, berikutnya, boleh, DESAIN, hanyaBunyi, muatUlangSekarang, skala, suaraIndonesia, teksMundur } from "@/lib/layarProduksi";
import { perkiraanDetik, potongUcapan, putarNada, putarPindah } from "@/lib/layarSuara";
import { detikSejak, formatDurasi, offsetJam } from "@/lib/timer";
import { adaVersiBaru, INTERVAL_CEK_MS, versiSah } from "@/lib/versi";
import { Logo } from "@/components/Logo";
import { TvKode } from "@/components/TvKode";
import * as W from "@/lib/waktu";

// Owner 2026-10-15/16 (requirement 61): layar produksi for the Xiaomi TV A32 (browser).
// Every post in one 16:9 screen (drawn at 1280×720, scaled to the TV's viewport),
// deadlines, today's schedule with a countdown, running text, and spoken announcements.
// Browsers block sound until a person presses a button: "Aktifkan suara" once per opening.
// v2.10 (owner 2026-10-19, requirement 70): an opening chime before every announcement (own Web Audio
// tones from the settings, firmer for a reject), the detailed periodic reminder spoken sentence by
// sentence (never cut; the next announcement waits), the last announcement in the bottom bar.
const LAYAR_MS = 12000;
const KEJADIAN_MS = 10000;
const SPS = typeof window !== "undefined" ? window.speechSynthesis : null;
const STATUS = {  // short words: the badge must fit next to the number on a 1/5-wide column
  REWORK: { teks: "REWORK", Icon: RotateCcw, gaya: "bg-red-600 text-white" },
  DIKERJAKAN: { teks: "PROSES", Icon: PlayCircle, gaya: "bg-soq-oranye text-soq-biru-tua" },
  MENUNGGU: { teks: "TUNGGU", Icon: Clock, gaya: "bg-slate-200 text-slate-800" },
};
const KARTU_TAMPIL = 2;  // what fits per column at 1280×720 with letters readable from 4–5 m

const Kartu = ({ k, timer, kiniMs }) => {
  const s = STATUS[k.status] || STATUS.MENUNGGU;
  const detik = timer && k.status === "DIKERJAKAN" && k.mulai ? detikSejak(k.mulai, kiniMs) : null;  // v2.16-A: running timer
  return (
    <div data-testid={`tvw-kartu-${k.nomor_spk}`} className="rounded-lg border-2 border-slate-200 bg-white px-2.5 py-1.5 leading-tight">
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono text-[28px] font-extrabold text-soq-biru-tua">{k.nomor}</span>
        {detik != null && <span data-testid={`tvw-timer-${k.nomor_spk}`} className="font-mono text-[20px] font-bold tabular-nums text-soq-oranye">{formatDurasi(detik)}</span>}
        <span className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[14px] font-bold ${s.gaya}`}><s.Icon size={14} />{s.teks}</span>
      </div>
      {k.pelanggan && <div className="truncate text-[19px] font-bold text-slate-900">{k.pelanggan}</div>}
      {k.cs && <div className="truncate text-[15px] font-semibold text-slate-500">CS {k.cs}</div>}
      {/* v2.17: why it is redone and from which station (QC or a station's report) */}
      {k.alasan && <div data-testid={`tvw-alasan-${k.nomor_spk}`} className="truncate text-[15px] font-bold text-red-700">dari {k.dari}: {k.alasan}</div>}
    </div>
  );
};

const Kolom = ({ k, timer, kiniMs }) => (
  <div data-testid={`tvw-kolom-${k.judul}`} className="flex min-h-0 flex-col overflow-hidden rounded-xl border-2 border-soq-biru/30 bg-slate-50">
    <div className="flex items-center justify-between bg-soq-biru px-2.5 py-1.5 text-white">
      <span className="truncate font-judul text-[22px] uppercase leading-none">{k.judul}</span>
      <span className="rounded-full bg-soq-oranye px-2 text-[20px] font-extrabold text-soq-biru-tua">{k.total}</span>
    </div>
    <div className="flex-1 space-y-1.5 overflow-hidden p-1.5">
      {k.kartu.slice(0, KARTU_TAMPIL).map((c) => <Kartu key={`${c.nomor_spk}-${c.status}`} k={c} timer={timer} kiniMs={kiniMs} />)}
      {k.total === 0 && <div className="pt-6 text-center text-[17px] text-slate-400">Kosong</div>}
    </div>
    {k.total > KARTU_TAMPIL && <div className="bg-slate-200 py-0.5 text-center text-[16px] font-bold text-slate-700">+{k.total - KARTU_TAMPIL} lainnya</div>}
  </div>
);

export default function LayarProduksi() {
  const [perluKode, setPerluKode] = React.useState(false);
  const [pesanKode, setPesanKode] = React.useState(null);  // v2.15: 403 "minta izin ke Owner" shown above the code form
  const [d, setD] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [kini, setKini] = React.useState(new Date());
  const [ukuran, setUkuran] = React.useState({ w: window.innerWidth, h: window.innerHeight });
  const [suaraAktif, setSuaraAktif] = React.useState(false);
  const [suara, setSuara] = React.useState(null);  // Indonesian voice or null
  const [tampil, setTampil] = React.useState(null);  // announcement on screen
  const [terakhirDiucap, setTerakhirDiucap] = React.useState(null);
  const antrean = React.useRef([]);
  const sejak = React.useRef(null);
  const sibuk = React.useRef(false);
  const audio = React.useRef(null);
  const dRef = React.useRef(null);
  dRef.current = d;
  const offset = React.useRef(0);  // v2.16-A: server clock − browser clock, for the running timer

  const muatLayar = React.useCallback(async () => {
    try { const r = await apiTvwLayar(); offset.current = offsetJam(r.jam, Date.now()); setD(r); setError(null); setPerluKode(false); }
    catch (e) {
      if (e.response?.status === 401) { setPerluKode(true); setPesanKode(null); }
      else if (e.response?.status === 403) { setPerluKode(true); setPesanKode(e.response?.data?.detail || "Akun Anda tidak diberi izin melihat layar produksi — minta izin ke Owner."); }
      else setError("Koneksi terputus. Mencoba lagi…");
    }
  }, []);

  const ucapkan = React.useCallback(() => {
    if (sibuk.current || !antrean.current.length) return;
    const k = antrean.current.shift();
    sibuk.current = true;
    const tenang = dRef.current?.suara?.sekarang_tenang;
    const bunyi = dRef.current?.suara?.bunyi || { aktif: true, nada: "DINGDONG", volume: 70 };
    if (hanyaBunyi(k)) {  // v2.16-A: a short sound only, nothing shown or spoken; v2.18.1: otherwise read out
      const ms = suaraAktif && !tenang && bunyi.aktif ? putarPindah(audio.current, bunyi.volume) : 0;
      setTimeout(() => { sibuk.current = false; ucapkan(); }, ms + 300);
      return;
    }
    setTampil(k);
    const selesai = () => { sibuk.current = false; setTampil(null); setTerakhirDiucap(k); setTimeout(() => ucapkan(), 800); };
    const bacaMs = Math.min(20000, Math.max(7000, (k.ucapan || "").length * 60));  // no voice: time to read the text
    if (!suaraAktif || tenang) { setTimeout(selesai, bacaMs); return; }
    const nadaMs = bunyi.aktif ? putarNada(audio.current, { nada: bunyi.nada, volume: bunyi.volume, tegas: k.prioritas === 1 }) : 0;
    if (SPS && suara) {
      // v2.16-A (butir 9c): a reject is read out `reject_ulang` times (owner: 2 by default)
      const ulang = k.jenis === "REJECT" ? Math.min(3, Math.max(1, Number(dRef.current?.suara?.reject_ulang) || 2)) : 1;
      const bagian = Array.from({ length: ulang }, () => potongUcapan(k.ucapan)).flat();
      let beres = false;
      const akhir = () => { if (!beres) { beres = true; setTimeout(selesai, 1500); } };
      const bicara = (i) => {
        if (beres) return;
        if (i >= bagian.length) { akhir(); return; }
        const u = new SpeechSynthesisUtterance(bagian[i]);
        u.lang = "id-ID"; u.voice = suara; u.rate = 0.95;
        let lanjut = false;
        const next = () => { if (!lanjut) { lanjut = true; bicara(i + 1); } };
        u.onend = next; u.onerror = next;
        SPS.speak(u);
        setTimeout(next, perkiraanDetik(bagian[i]) * 1000);  // some TV browsers never fire onend
      };
      setTimeout(() => bicara(0), nadaMs + 400);
    } else {
      setTimeout(selesai, nadaMs + bacaMs);  // no Indonesian voice: chime + big text only
    }
  }, [suaraAktif, suara]);

  const muatKejadian = React.useCallback(async () => {
    try {
      const r = await apiTvwKejadian(sejak.current);
      if (sejak.current == null) { sejak.current = r.terakhir; return; }  // a TV that just opened starts from now
      const aktif = dRef.current?.suara?.aktif;
      const baru = r.kejadian.filter((k) => boleh(k, aktif) && Date.now() - new Date(k.waktu).getTime() < 10 * 60 * 1000);
      if (r.kejadian.length) sejak.current = Math.max(...r.kejadian.map((k) => k.seq));
      antrean.current = antre(antrean.current, baru);
      ucapkan();
    } catch (e) { if (e.response?.status === 401 || e.response?.status === 403) setPerluKode(true); }
  }, [ucapkan]);

  React.useEffect(() => { muatLayar(); const t = setInterval(muatLayar, LAYAR_MS); return () => clearInterval(t); }, [muatLayar]);
  React.useEffect(() => { if (perluKode) return undefined; muatKejadian(); const t = setInterval(muatKejadian, KEJADIAN_MS); return () => clearInterval(t); }, [muatKejadian, perluKode]);
  React.useEffect(() => { const t = setInterval(() => setKini(new Date()), 1000); return () => clearInterval(t); }, []);
  React.useEffect(() => {
    const f = () => setUkuran({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", f); return () => window.removeEventListener("resize", f);
  }, []);
  React.useEffect(() => {  // voices load late on many browsers
    if (!SPS) return undefined;
    const pilih = () => setSuara(suaraIndonesia(SPS.getVoices()));
    pilih(); SPS.addEventListener?.("voiceschanged", pilih);
    return () => SPS.removeEventListener?.("voiceschanged", pilih);
  }, []);
  React.useEffect(() => {  // a new app version: reload only in the quiet hours
    const lokal = process.env.REACT_APP_VERSI;
    if (!versiSah(lokal)) return undefined;
    const cek = async () => {
      try {
        const { versi } = await (await fetch("/version.json", { cache: "no-store" })).json();
        if (muatUlangSekarang(adaVersiBaru(lokal, versi), dRef.current?.suara?.sekarang_tenang)) window.location.reload();
      } catch { /* offline: next time */ }
    };
    const t = setInterval(cek, INTERVAL_CEK_MS); return () => clearInterval(t);
  }, []);

  const aktifkan = () => {
    try { const C = window.AudioContext || window.webkitAudioContext; audio.current = audio.current || (C ? new C() : null); audio.current?.resume?.(); } catch { audio.current = null; }
    if (SPS) { try { SPS.cancel(); SPS.speak(new SpeechSynthesisUtterance(" ")); } catch { /* ignore */ } }
    const b = dRef.current?.suara?.bunyi;
    if (!b || b.aktif) putarNada(audio.current, { nada: b?.nada, volume: b?.volume ?? 70 });
    setSuaraAktif(true);
  };

  if (perluKode) return <TvKode judul="Layar Produksi" pesan={pesanKode} onMasuk={() => { setPerluKode(false); setPesanKode(null); sejak.current = null; muatLayar(); }} />;
  if (!d) return <div className="flex min-h-screen items-center justify-center bg-soq-biru text-2xl text-white"><Loader2 className="mr-2 animate-spin" /> Memuat layar produksi…</div>;

  const s = skala(ukuran.w, ukuran.h);
  const hhmm = W.jam(kini.toISOString()).replace(".", ":");
  const next = berikutnya(d.jadwal, hhmm);
  const baris = Math.ceil(d.kolom.length / 2);
  return (
    <div data-testid="tvw" className="h-screen w-screen overflow-hidden bg-soq-biru-tua">
      <div style={{ width: DESAIN.lebar, height: DESAIN.tinggi, transform: `scale(${s})`, transformOrigin: "top left" }}
        className="relative flex flex-col bg-white">
        {/* top bar: logo, title, running text, clock */}
        <div className="flex h-[74px] items-center gap-4 bg-soq-biru px-4 text-white">
          <Logo varian="putih" className="h-10 w-auto" />
          <div className="min-w-0 flex-1 overflow-hidden rounded-lg bg-soq-biru-tua/60 py-1.5">
            <div data-testid="tvw-teks" className="tvw-jalan whitespace-nowrap text-[24px] font-semibold text-soq-oranye">{d.teks || d.nama_usaha}</div>
          </div>
          <div className="text-right leading-none">
            <div data-testid="tvw-jam" className="font-mono text-[40px] font-extrabold tabular-nums">{W.jamDetik(kini.toISOString())}</div>
            <div className="text-[16px]">{W.tanggalPanjang(kini.toISOString())} · {W.zonaSingkat()}</div>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 gap-2 p-2">
          <div data-testid="tvw-kolom" className="grid min-h-0 flex-1 gap-2" style={{ gridTemplateColumns: `repeat(${baris}, minmax(0, 1fr))`, gridTemplateRows: "repeat(2, minmax(0, 1fr))" }}>
            {d.kolom.map((k) => <Kolom key={k.judul} k={k} timer={Boolean(d.timer?.layar_produksi)} kiniMs={kini.getTime() + offset.current} />)}
          </div>
          <div className="flex w-[300px] shrink-0 flex-col gap-2">
            <div data-testid="tvw-tenggat" className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border-2 border-red-300 bg-red-50">
              <div className="flex items-center gap-2 bg-red-600 px-3 py-1.5 text-white"><AlertTriangle size={22} /><span className="font-judul text-[22px] uppercase">Deadline dekat</span></div>
              <div className="flex-1 space-y-1.5 overflow-hidden p-2">
                {d.tenggat.length === 0 && <div className="pt-4 text-center text-[17px] text-slate-500">Tidak ada dalam 24 jam</div>}
                {d.tenggat.slice(0, 5).map((t) => (
                  <div key={t.nomor_spk} className={`rounded-lg border-2 px-2 py-1 leading-tight ${t.merah ? "border-red-600 bg-white" : "border-amber-400 bg-white"}`}>
                    <div className="flex justify-between"><span className="font-mono text-[24px] font-extrabold text-soq-biru-tua">{t.nomor}</span>
                      <span className={`text-[20px] font-bold ${t.merah ? "text-red-600" : "text-amber-700"}`}>{t.sisa_menit < 0 ? "LEWAT" : teksMundur(t.sisa_menit).replace(" menit", "m").replace(" jam ", "j ")}</span></div>
                    <div className="truncate text-[16px] text-slate-700">{t.pelanggan || t.pos_nama}</div>
                  </div>
                ))}
              </div>
            </div>
            <div data-testid="tvw-jadwal" className="rounded-xl border-2 border-soq-biru/30 bg-slate-50">
              <div className="flex items-center gap-2 bg-soq-biru px-3 py-1.5 text-white"><Clock size={20} /><span className="font-judul text-[22px] uppercase">Jadwal hari ini</span></div>
              <div className="p-2">
                {d.jadwal.length === 0 && <div className="text-[16px] text-slate-500">Jadwal belum diatur Owner</div>}
                {next && <div data-testid="tvw-mundur" className="mb-1 rounded-lg bg-soq-oranye px-2 py-1 text-[20px] font-bold leading-tight text-soq-biru-tua">{next.label} {next.jam}<br /><span className="text-[18px]">{teksMundur(next.sisaMenit)} lagi</span></div>}
                <div className="flex flex-wrap gap-x-3 text-[16px] text-slate-700">{d.jadwal.map((j) => <span key={`${j.jam}${j.jenis}`}>{j.jam} {j.label}</span>)}</div>
              </div>
            </div>
          </div>
        </div>
        {/* bottom bar: sound status and the last announcement */}
        <div className="flex h-[40px] items-center gap-3 bg-slate-900 px-4 text-[17px] text-white">
          {suaraAktif ? <span data-testid="tvw-suara" className="inline-flex items-center gap-1 text-emerald-300"><Volume2 size={18} />{d.suara.sekarang_tenang ? "Jam tenang (tanpa suara)" : suara ? "Suara aktif" : "Nada aktif (suara Indonesia tidak tersedia)"}</span>
            : <span data-testid="tvw-suara" className="inline-flex items-center gap-1 text-amber-300"><VolumeX size={18} />Suara mati</span>}
          <span className="shrink-0 text-slate-300"><Bell size={16} className="inline" /></span>
          <div data-testid="tvw-terakhir" className="min-w-0 flex-1 overflow-hidden text-slate-200">
            {terakhirDiucap
              ? <div key={terakhirDiucap.seq} className={(terakhirDiucap.ucapan || "").length > 110 ? "tvw-jalan whitespace-nowrap" : "truncate"}
                  style={{ animationDuration: `${Math.max(22, Math.round((terakhirDiucap.ucapan || "").length / 9))}s` }}>
                  {["BERKALA", "MANUAL"].includes(terakhirDiucap.jenis) ? terakhirDiucap.ucapan : terakhirDiucap.teks}</div>
              : "Belum ada pengumuman"}
          </div>
          {error && <span className="ml-auto text-red-300">{error}</span>}
        </div>
        {tampil && (
          <div data-testid="tvw-pengumuman" className={`absolute inset-x-8 top-[150px] rounded-2xl border-8 p-6 text-center shadow-2xl ${tampil.prioritas === 1 ? "border-red-600 bg-red-50" : "border-soq-oranye bg-white"}`}>
            <div className={`${tampil.jenis === "BERKALA" ? "text-[34px]" : "text-[44px]"} font-extrabold leading-tight text-soq-biru-tua`}>{tampil.teks}</div>
            {tampil.jenis === "BERKALA" && (
              <div data-testid="tvw-pengumuman-rinci" className="mt-3 max-h-[380px] overflow-hidden text-left text-[21px] leading-snug text-slate-800">{tampil.ucapan}</div>
            )}
          </div>
        )}
        {!suaraAktif && (
          <button data-testid="tvw-aktifkan-suara" autoFocus onClick={aktifkan}
            className="absolute bottom-[56px] right-4 rounded-2xl border-4 border-white bg-soq-oranye px-6 py-3 text-[26px] font-extrabold text-soq-biru-tua shadow-xl">
            <Volume2 className="mr-2 inline" size={28} /> Aktifkan suara (tekan OK)</button>
        )}
      </div>
    </div>
  );
}
